import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Loader2, Plus, Trash2, Lightbulb, ExternalLink, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IP_TYPES = [
  { value: "trademark",     label: "Trademark" },
  { value: "patent",        label: "Patent" },
  { value: "copyright",     label: "Copyright" },
  { value: "trade_secret",  label: "Trade Secret" },
  { value: "domain",        label: "Domain Name" },
];

const STATUSES = [
  { value: "filed",      label: "Filed",       color: "bg-blue-100 text-blue-700" },
  { value: "pending",    label: "Pending",     color: "bg-amber-100 text-amber-700" },
  { value: "granted",    label: "Granted",     color: "bg-emerald-100 text-emerald-700" },
  { value: "abandoned",  label: "Abandoned",   color: "bg-gray-100 text-gray-700" },
  { value: "expired",    label: "Expired",     color: "bg-rose-100 text-rose-700" },
];

const EMPTY = {
  title: "", ip_type: "trademark", registration_number: "", jurisdiction: "",
  filing_date: "", grant_date: "", expiry_date: "",
  status: "filed", description: "", document_url: "", notes: "",
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : "—";

export default function IPPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner-console/ip`);
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load IP records");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setDialogOpen(true); };
  const openEdit = (ip) => {
    setForm({
      title: ip.title || "", ip_type: ip.ip_type || "trademark",
      registration_number: ip.registration_number || "", jurisdiction: ip.jurisdiction || "",
      filing_date: ip.filing_date || "", grant_date: ip.grant_date || "", expiry_date: ip.expiry_date || "",
      status: ip.status || "filed", description: ip.description || "",
      document_url: ip.document_url || "", notes: ip.notes || "",
    });
    setEditingId(ip.id);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/owner-console/ip/${editingId}`, form);
        toast.success("IP record updated");
      } else {
        await axios.post(`${API}/owner-console/ip`, form);
        toast.success("IP record added");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this IP record? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/owner-console/ip/${id}`);
      toast.success("Deleted");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="ip-panel">
      <Card className="border-violet-200/40">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-violet-600" />
                Intellectual Property
              </CardTitle>
              <CardDescription>
                Patents, trademarks, copyrights, trade secrets, and brand-critical domains. Centralised so renewal dates and registration numbers are never lost.
              </CardDescription>
            </div>
            <Button onClick={openNew} data-testid="add-ip-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add IP Record
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No IP records yet. Suggested starter entries:
              <div className="mt-2 text-xs space-y-1">
                <div><strong>Trademark:</strong> "Business Genie" word mark + logo</div>
                <div><strong>Copyright:</strong> Source code + database schema</div>
                <div><strong>Domain:</strong> yourbusinessgenie.com (auto-renewal, registrar contact)</div>
                <div><strong>Trade Secret:</strong> Internal AI prompt templates + pricing algorithms</div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(ip => {
                const typeInfo = IP_TYPES.find(t => t.value === ip.ip_type) || IP_TYPES[0];
                const statusInfo = STATUSES.find(s => s.value === ip.status) || STATUSES[0];
                return (
                  <div key={ip.id} className="p-3 rounded-md border bg-card hover:bg-accent/30 transition-colors" data-testid={`ip-row-${ip.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{ip.title}</span>
                          <Badge variant="outline" className="text-xs">{typeInfo.label}</Badge>
                          <Badge className={`text-xs ${statusInfo.color}`}>{statusInfo.label}</Badge>
                          {ip.registration_number && <Badge variant="secondary" className="text-xs">#{ip.registration_number}</Badge>}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground space-x-2">
                          {ip.jurisdiction && <span>{ip.jurisdiction}</span>}
                          {ip.filing_date && <span>· Filed {fmtDate(ip.filing_date)}</span>}
                          {ip.grant_date && <span>· Granted {fmtDate(ip.grant_date)}</span>}
                          {ip.expiry_date && <span>· Expires {fmtDate(ip.expiry_date)}</span>}
                        </div>
                        {ip.description && <div className="mt-1 text-xs">{ip.description}</div>}
                      </div>
                      <div className="flex items-center gap-1">
                        {ip.document_url && (
                          <a href={ip.document_url} target="_blank" rel="noopener noreferrer">
                            <Button size="icon" variant="ghost" title="Open document"><ExternalLink className="h-4 w-4" /></Button>
                          </a>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => openEdit(ip)} data-testid={`edit-ip-${ip.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(ip.id)} data-testid={`delete-ip-${ip.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit IP Record" : "Add Intellectual Property"}</DialogTitle>
            <DialogDescription>Track filing/grant/expiry dates and registration details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Business Genie word mark" data-testid="ip-title-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.ip_type} onValueChange={(v) => setForm({ ...form, ip_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {IP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Registration Number</Label>
              <Input value={form.registration_number} onChange={e => setForm({ ...form, registration_number: e.target.value })} placeholder="USPTO Serial / CIPO #" />
            </div>
            <div>
              <Label>Jurisdiction</Label>
              <Input value={form.jurisdiction} onChange={e => setForm({ ...form, jurisdiction: e.target.value })} placeholder="USA / Canada / EU / WIPO" />
            </div>
            <div>
              <Label>Filing Date</Label>
              <Input type="date" value={form.filing_date || ""} onChange={e => setForm({ ...form, filing_date: e.target.value })} />
            </div>
            <div>
              <Label>Grant Date</Label>
              <Input type="date" value={form.grant_date || ""} onChange={e => setForm({ ...form, grant_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Expiry Date</Label>
              <Input type="date" value={form.expiry_date || ""} onChange={e => setForm({ ...form, expiry_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What does this protect?" />
            </div>
            <div className="sm:col-span-2">
              <Label>Document URL</Label>
              <Input value={form.document_url} onChange={e => setForm({ ...form, document_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Lawyer contact, renewal cost, dependencies" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-ip-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {editingId ? "Update" : "Save IP Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
